import { useEffect, useId, useRef, useState } from 'react'

// FilmReel — lucide-compatible custom icon (size / color / strokeWidth props).
// Drop-in replacement for lucide icons in BottomNav; matches the reel glyph
// used by WatchPlanButton in TonightMode for visual consistency.
//
// Glyph ported from Design's "recolorable premium glyph" pass (2026-08-06):
// every facet is color-mix(var(--ic), white/black) off the single `color`
// prop rather than a fixed gradient, so it stays correct whether `color` is
// a hex, currentColor, or a themed CSS var — the same requirement that kept
// the FIRST pass (a hardcoded gold gradient) from shipping. A static
// low-opacity white shine sells "polished metal" independent of hue.
//
// Optional `active` prop: when it flips false -> true (a show being added to
// tonight's plans), the reel spins up and an amber ring blooms — "threading the
// projector." When `active` is omitted the icon renders static, so existing
// callers (the nav) are unaffected.
const REEL_HUB_ANGLES = [0, 60, 120, 180, 240, 300]

export function FilmReel({ size = 24, color = "currentColor", strokeWidth = 1.8, active }) {
  const [burst, setBurst] = useState(false)
  const prev = useRef(active)
  useEffect(() => {
    if (active && !prev.current) {
      setBurst(true)
      const t = setTimeout(() => setBurst(false), 900)
      prev.current = active
      return () => clearTimeout(t)
    }
    prev.current = active
  }, [active])

  const uid = useId().replace(/:/g, '')
  const clipId = `reelClip-${uid}`

  // reelSpinFRI/reelGlow keyframes live in index.css — see MoonIcon.jsx's
  // comment for why an inline <style> inside <svg> is unsafe.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
      style={{ overflow: 'visible', '--ic': color }}>
      {burst && (
        <circle cx={12} cy={12} r={11.5} fill="none" stroke={color} strokeWidth={1.5}
          style={{ transformBox: 'view-box', transformOrigin: '12px 12px', opacity: 0, '--nxa': 'reelGlow 0.85s ease-out' }} />
      )}
      <g style={{ transformBox: 'view-box', transformOrigin: '12px 12px', '--nxa': burst ? 'reelSpinFRI 0.85s cubic-bezier(0.2,0.75,0.2,1)' : undefined }}>
        <defs>
          <clipPath id={clipId}><circle cx="12" cy="12" r="10.5" /></clipPath>
        </defs>
        <circle cx="12" cy="12" r="10.5" fill="color-mix(in srgb, var(--ic) 16%, transparent)" stroke="color-mix(in srgb, var(--ic) 88%, black 12%)" strokeWidth={strokeWidth} />
        <circle cx="12" cy="12" r="10.5" fill="none" stroke="color-mix(in srgb, var(--ic) 55%, white 45%)" strokeWidth={strokeWidth * 0.4} opacity="0.5" />
        {REEL_HUB_ANGLES.map(deg => (
          <g key={deg} transform={`rotate(${deg} 12 12)`}>
            <ellipse cx="12" cy="6.1" rx="2.8" ry="2.5" fill="color-mix(in srgb, var(--ic) 30%, transparent)" stroke="color-mix(in srgb, var(--ic) 75%, black 10%)" strokeWidth={strokeWidth * 0.6} />
          </g>
        ))}
        <circle cx="12" cy="12" r="3.1" fill="color-mix(in srgb, var(--ic) 92%, white 8%)" stroke="color-mix(in srgb, var(--ic) 80%, black 20%)" strokeWidth={strokeWidth * 0.65} />
        <circle cx="12" cy="12" r="1.05" fill="var(--nav-bg, #07070f)" />
        <g clipPath={`url(#${clipId})`}>
          <ellipse cx="7.5" cy="7" rx="7" ry="3.5" fill="white" opacity="0.14" transform="rotate(-24 7.5 7)" />
        </g>
      </g>
    </svg>
  )
}

// ── FilmReelSpinner — full loading-state spinner (LazyFallback and anywhere else
// a page-level "working" state needs more presence than a bare border-ring). Ported
// from Jason's Claude Design export ("Animated film reel with coil"): a brushed-metal
// reel disc with 6 canister-style spoke lobes, a hub, rim rivets, and a soft highlight,
// continuously spinning. Purely decorative — reduced-motion users get a static disc.
// `useId` keys the gradient/filter defs so multiple instances on one page (unlikely,
// but LazyFallback can theoretically stack during a fast route change) never collide.
const REEL_SPOKE_ANGLES = [0, 60, 120, 180, 240, 300]
const REEL_RIM_ANGLES   = [0, 60, 120, 180, 240, 300]

export function FilmReelSpinner({ size = 96, speed = 3.5, style }) {
  const uid = useId().replace(/:/g, '')
  const discGrad = `frsDisc-${uid}`, hubGrad = `frsHub-${uid}`, edgeGrad = `frsEdge-${uid}`, shadow = `frsShadow-${uid}`

  const spokes = REEL_SPOKE_ANGLES.map(a => {
    const rad = (a * Math.PI) / 180
    const cx = 200 + Math.cos(rad) * 88, cy = 200 + Math.sin(rad) * 88
    return { a, cx, cy, rot: `rotate(${a} ${cx} ${cy})` }
  })
  const rim = REEL_RIM_ANGLES.map(a => {
    const rad = (a * Math.PI) / 180
    return { a, cx: 200 + Math.cos(rad) * 40, cy: 200 + Math.sin(rad) * 40 }
  })

  // filmReelSpin keyframes + .film-reel-spin live in index.css — see
  // MoonIcon.jsx's comment for why an inline <style> inside <svg> is unsafe.
  return (
    <svg viewBox="0 0 400 400" width={size} height={size} aria-hidden="true" style={style}>
      <defs>
        <radialGradient id={discGrad} cx="38%" cy="32%" r="75%">
          <stop offset="0%" stopColor="#e6e6e6" /><stop offset="55%" stopColor="#b8b8b8" /><stop offset="100%" stopColor="#8a8a8a" />
        </radialGradient>
        <radialGradient id={hubGrad} cx="35%" cy="30%" r="75%">
          <stop offset="0%" stopColor="#dcdcdc" /><stop offset="100%" stopColor="#8f8f8f" />
        </radialGradient>
        <linearGradient id={edgeGrad} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6e6e6e" /><stop offset="100%" stopColor="#3c3c3c" />
        </linearGradient>
        <filter id={shadow} x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="10" stdDeviation="10" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>
      <circle cx="200" cy="212" r="150" fill={`url(#${edgeGrad})`} />
      <g className="film-reel-spin" style={{ transformBox: 'view-box', transformOrigin: '200px 200px', animationDuration: `${speed}s`, filter: `url(#${shadow})` }}>
        <circle cx="200" cy="200" r="150" fill={`url(#${discGrad})`} stroke="#333" strokeWidth="6" />
        <circle cx="200" cy="200" r="132" fill="none" stroke="#555" strokeWidth="2.5" opacity="0.5" />
        {spokes.map(s => (
          <g key={s.a}>
            <ellipse cx={s.cx} cy={s.cy} rx="30" ry="26" transform={s.rot} fill="#1e1e1e" />
            <ellipse cx={s.cx} cy={s.cy} rx="30" ry="26" transform={s.rot} fill="none" stroke="#000" strokeWidth="1.5" opacity="0.5" />
          </g>
        ))}
        <circle cx="200" cy="204" r="30" fill="#5a5a5a" />
        <circle cx="200" cy="200" r="30" fill={`url(#${hubGrad})`} stroke="#333" strokeWidth="4" />
        <circle cx="200" cy="200" r="9" fill="#1a1a1a" />
        {rim.map(r => <circle key={r.a} cx={r.cx} cy={r.cy} r="6" fill="#1a1a1a" />)}
        <ellipse cx="150" cy="150" rx="60" ry="34" fill="#fff" opacity="0.18" transform="rotate(-25 150 150)" />
      </g>
    </svg>
  )
}
